<template>
    <div>
      <div v-if="isAuthenticated">
        <div class="player-info">
            <h2 class="player-info__title">Tilastot</h2>
            <div class="player-info__grid">
              <div class="stat-item">
                <span class="stat-label">Pisteet yhteensä</span>
                <span class="stat-value">{{ totalPoints }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Sijoitus</span>
                <span class="stat-value">{{ playerRank !== null ? playerRank : 'Ladataan...' }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Vaihdot jäljellä</span>
                <span class="stat-value">{{ changesLeft }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Rahaa jäljellä</span>
                <div class="stat-with-image">
                  <span class="stat-value">{{ displayMoney.toLocaleString() }}</span>
                  <img src="@/assets/kolikot.webp" alt="Kolikot" class="stat-icon" />
                </div>
              </div>
              <div class="stat-item">
                <span class="stat-label">Startit (hevoset)</span>
                <span class="stat-value">{{ horseStarts }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Voitot (hevoset)</span>
                <span class="stat-value">{{ horseWins }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Johtokerrat (hevoset)</span>
                <span class="stat-value">{{ horseLeadCount }}</span>
              </div>
              <div class="stat-item">
                <span class="stat-label">Voittosumma (hevoset)</span>
                <span class="stat-value">{{ horsePrizeSum.toLocaleString() }} €</span>
              </div>
            </div>
            <DailyPointsChart
              :points-breakdown="pointsBreakdown"
              @view-horse="openHorsePerformanceModal"
              @view-jockey="openJockeyPerformanceModal"
            />
            <button
              @click="togglePointsBreakdown"
              class="player-info__toggle"
            >
              {{ showPointsBreakdown ? 'Piilota' : 'Näytä' }} pistesuoritukset
            </button>
            <transition name="fade-height">
              <div
                v-if="showPointsBreakdown"
                class="points-breakdown"
              >
                <h3 class="points-breakdown__title">Pistesuoritukset</h3>
                <ul v-if="pointsBreakdown.length" class="points-breakdown__list">
                  <li
                    v-for="(item, index) in pointsBreakdown"
                    :key="item.selectionKey"
                    :class="['points-breakdown__row', { 'date-separator-top': shouldShowDateSeparator(index) }]"
                  >
                    <span class="pb-left">
                      <span>
                        <span class="breakdown-date-type">
                          {{ item.date }}:
                        </span>
                        <span
                          v-if="isBreakdownLink(item)"
                          class="breakdown-link"
                          @click="handleBreakdownClick(item)"
                          :title="'Näytä ' + item.name + ' suoritukset'"
                        >
                          {{ item.name }} <span class="info-icon-small">ℹ️</span>
                        </span>
                        <span v-else>
                          {{ item.name }}
                        </span>
                        <span class="breakdown-date-type">
                          ({{ item.type === 'Horse' ? 'Hevonen' : item.type === 'Jockey' ? 'Ohjastaja' : item.type }})
                        </span>
                      </span>
                    </span>
                    <span class="pb-right">{{ item.points }} pistettä</span>
                  </li>
                </ul>
                <p v-else class="points-breakdown__empty">
                  Ei pisteitä vielä ansaittu.
                </p>
              </div>
            </transition>
        </div>
        
        <!-- Premium Upgrade Section -->
        <!-- Render only when we have explicitly confirmed the user is NOT paid. -->
        <div v-if="isPaidUser === false" class="premium-upgrade-section">
          <div class="premium-upgrade-card">
            <div class="premium-header">
              <h3 class="premium-title">🌟 Päivitä Premium-versioon</h3>
              <div class="premium-price">3,20€</div>
            </div>
            <div class="premium-benefits">
              <h4>Premium-edut:</h4>
              <ul>
                <li>✅ Pisteiden kerääminen jakson loppuun asti</li>
                <li>✅ Ei 2 viikon rajoitusta</li>
                <li>✅ 15 vaihtoa käytettävissä</li>
              </ul>
            </div>
            <div class="premium-actions">
              <button 
                @click="confirmPremiumUpgrade" 
                class="premium-upgrade-btn"
                :disabled="isGeneratingInvoice"
              >
                {{ isGeneratingInvoice ? 'Luodaan laskua...' : 'Päivitä Premium-versioon' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Premium Upgrade Confirmation Modal -->
        <div v-if="showPremiumUpgrade" class="modal-overlay" @click="cancelPremiumUpgrade">
          <div class="modal-content premium-modal" @click.stop>
            <button @click="cancelPremiumUpgrade" class="close-modal">&times;</button>
            <div class="premium-confirmation">
              <h3>Vahvista Premium-tilaus</h3>
              <div class="confirmation-details">
                <p><strong>Hinta:</strong> 3,20€</p>
                <p><strong>Toimitus:</strong> Premium-ominaisuudet aktivoituvat välittömästi</p>
                <p><strong>Maksu:</strong> Lasku lähetetään sähköpostiisi</p>
                <p><strong>Eräpäivä:</strong> 2 päivää laskun päivämäärästä</p>
              </div>
              
              <div class="payment-notice">
                <div class="notice-icon">ℹ️</div>
                <div class="notice-content">
                  <p><strong>Tärkeää tietää:</strong></p>
                  <ul>
                    <li>Käytä laskun viitenumeroa maksaessasi - muuten maksu ei kohdistu tilillesi</li>
                    <li>Jos et maksa laskua, ei hätää! Premium-oikeus vain poistuu automaattisesti</li>
                    <li>Emme lähetä muistutuksia tai peri lisäkuluja</li>
                  </ul>
                </div>
              </div>
              <div class="premium-benefits-modal">
                <h4>🌟 Premium-version edut:</h4>
                <div class="benefits-grid">
                  <div class="benefit-item">
                    <div class="benefit-icon">⏰</div>
                    <div class="benefit-text">
                      <strong>Koko jakso käytössä</strong>
                      <span>Ei 2 viikon rajoitusta</span>
                    </div>
                  </div>
                  <div class="benefit-item">
                    <div class="benefit-icon">🔄</div>
                    <div class="benefit-text">
                      <strong>15 vaihtoa</strong>
                      <span>Täysi joustavuus</span>
                    </div>
                  </div>
                </div>
              </div>
              
              <!-- EU Consumer Rights Compliance -->
              <div class="eu-compliance-section">
                <div class="checkbox-container">
                  <input 
                    type="checkbox" 
                    id="cancelRightWaiver" 
                    v-model="cancelRightWaived"
                    :disabled="isGeneratingInvoice"
                  />
                  <label for="cancelRightWaiver" class="checkbox-label">
                    Hyväksyn, että suoritus alkaa välittömästi ja menetän 14 päivän peruuttamisoikeuden.
                  </label>
                </div>
                
                <div class="checkbox-container">
                  <input 
                    type="checkbox" 
                    id="termsAcceptance" 
                    v-model="termsAccepted"
                    :disabled="isGeneratingInvoice"
                  />
                  <label for="termsAcceptance" class="checkbox-label">
                    Hyväksyn 
                    <button @click="showTermsModal = true" class="terms-link-button" type="button">
                      käyttöehdot
                    </button>
                    (päivitetty 6.7.2025).
                  </label>
                </div>
              </div>
              
              <div class="confirmation-buttons">
                <button 
                  @click="generateInvoice" 
                  class="confirm-premium-btn eu-compliant"
                  :disabled="isGeneratingInvoice || !cancelRightWaived || !termsAccepted"
                >
                  {{ isGeneratingInvoice ? 'Luodaan laskua...' : 'Osta ja maksa 3,20€' }}
                </button>
                <button @click="cancelPremiumUpgrade" class="cancel-premium-btn">
                  Peruuta
                </button>
              </div>
            </div>
          </div>
        </div>
        
        <div v-if="changesDisabled" class="race-status-alert" :class="{'period-ended': periodEnded || racesCompleted}">
          <span class="race-status-icon">🏇</span>
          {{ changesDisabledMessage }}
        </div>

        <!-- Unconfirmed Changes Notification Banner -->
        <div v-if="hasUnconfirmedChanges" class="unconfirmed-changes-banner">
          <div class="banner-content">
            <div class="banner-icon">⚠️</div>
            <div class="banner-text">
              <h3>Sinulla on vahvistamattomia muutoksia</h3>
              <p>Muutokset tulevat voimaan vasta kun vahvistat ne.</p>
            </div>
            <button @click="confirmChanges" class="banner-confirm-btn" 
              :disabled="isConfirming || changesDisabled || isTeamOverpriced"
              :title="changesDisabled ? changesDisabledMessage : 
                     isTeamOverpriced ? 'Remmi on ylihintainen. Sinulla ei ole tarpeeksi rahaa.' : ''">
              {{ isConfirming ? 'Vahvistetaan...' : 'Vahvista muutokset' }}
            </button>
          </div>
        </div>
  
        <div class="flex flex-wrap gap-4 mb-6">
          <div class="selection flex-1 min-w-[250px] p-4 border rounded-lg shadow">
            <h2 class="text-xl font-semibold mb-3">Hevoset</h2>
            <div class="list space-y-2 mb-3">
              <div class="list-item p-2 border rounded" 
                   v-for="(horse, index) in pendingHorses" 
                   :key="horse.horse_id">
                <div class="horse-item-content">
                  <div class="horse-image">
                    <img src="@/assets/heppa2.webp" alt="Horse" class="horse-thumbnail" />
                  </div>
                  <div class="horse-details">
                    <h3
                      class="font-medium clickable-name horse-name"
                      @click="openHorsePerformanceModal(horse.horse_id, horse.horse_name)" 
                      title="Näytä suoritukset"
                    >
                      <span class="name-text">{{ horse.horse_name }}</span>
                      <span class="info-icon">ℹ️</span>
                      <span v-if="horse.racing === 'today'" class="racing-badge today-badge">Kilpailee tänään</span>
                      <span v-if="horse.racing === 'yes'" class="racing-badge soon-badge">Lähtölistoilla</span>
                    </h3>
                    <p v-if="formatUpcomingStarts(horse)" class="horse-start-dates">
                      {{ formatUpcomingStarts(horse) }}
                    </p>
                    <p class="text-sm">Hinta: {{ horse.price !== undefined && horse.price !== null ? horse.price.toLocaleString() : 'Ladataan...' }}</p>
                    <button 
                      @click="triggerChangeHorse(index)" 
                      class="mt-1 px-2 py-1 text-xs rounded"
                      :disabled="selectionButtonsDisabled"
                      :class="{'disabled-button': selectionButtonsDisabled}"
                      :title="changesDisabled ? changesDisabledMessage : 
                             (hasConfirmedChanges ? 'Peru vahvistetut muutokset ensin' : 
                             (hasAnySelections && changesLeft <= 0 && !beforeGamePeriod ? 'Ei vaihtoja jäljellä' : ''))"
                    >
                      Vaihda
                    </button>
                  </div>
                </div>
              </div>
              <div v-for="n in (5 - pendingHorses.length)" :key="'placeholder-horse-' + n" class="list-item p-2 border rounded text-center text-gray-400 italic">
                Tyhjä paikka
              </div>
            </div>
            <button 
              class="add-button w-full px-3 py-2 rounded disabled:opacity-50" 
              v-if="pendingHorses.length < 5" 
              @click="triggerAddHorse"
              :disabled="selectionButtonsDisabled"
              :class="{'disabled-button': selectionButtonsDisabled}"
              :title="changesDisabled ? changesDisabledMessage : 
                     (hasConfirmedChanges ? 'Peru vahvistetut muutokset ensin' : 
                     (hasAnySelections && changesLeft <= 0 && !beforeGamePeriod ? 'Ei vaihtoja jäljellä' : ''))"
            >
              Lisää hevonen
            </button>
            
            <!-- Racing legend -->
            <!-- Racing legend removed as it's now shown as badges -->
          </div>
  
          <div class="selection flex-1 min-w-[250px] p-4 border rounded-lg shadow">
            <h2 class="text-xl font-semibold mb-3">Ohjastaja</h2>
            <div class="list space-y-2 mb-3">
              <div class="list-item p-2 border rounded" v-if="pendingJockey">
                <div class="horse-item-content">
                  <div class="horse-image">
                    <img src="@/assets/jockey.webp" alt="Jockey" class="horse-thumbnail" />
                  </div>
                  <div class="horse-details">
                    <h3
                      class="font-medium clickable-name jockey-name"
                      @click="openJockeyPerformanceModal(pendingJockey.jockey_id, pendingJockey.jockey_name)" 
                      title="Näytä suoritukset"
                    >
                      <span class="name-text">{{ pendingJockey.jockey_name }}</span>
                      <span class="info-icon">ℹ️</span>
                    </h3>
                    <p class="text-sm">Hinta: {{ pendingJockey.price !== undefined && pendingJockey.price !== null ? pendingJockey.price.toLocaleString() : 'Ladataan...' }}</p>
                    <button 
                      @click="triggerChangeJockey" 
                      class="mt-1 px-2 py-1 text-xs rounded"
                      :disabled="selectionButtonsDisabled"
                      :class="{'disabled-button': selectionButtonsDisabled}"
                      :title="changesDisabled ? changesDisabledMessage : 
                             (hasConfirmedChanges ? 'Peru vahvistetut muutokset ensin' : 
                             (hasAnySelections && changesLeft <= 0 && !beforeGamePeriod ? 'Ei vaihtoja jäljellä' : ''))"
                    >
                      Vaihda
                    </button>
                  </div>
                </div>
              </div>
              <div v-else class="list-item p-2 border rounded text-center">
                <button 
                  class="add-button w-full px-3 py-2 rounded" 
                  @click="triggerSelectJockey"
                  :disabled="selectionButtonsDisabled"
                  :class="{'disabled-button': selectionButtonsDisabled}"
                  :title="changesDisabled ? changesDisabledMessage : 
                         (hasConfirmedChanges ? 'Peru vahvistetut muutokset ensin' : 
                         (hasAnySelections && changesLeft <= 0 && !beforeGamePeriod ? 'Ei vaihtoja jäljellä' : ''))"
                >
                  Valitse ohjastaja
                </button>
              </div>
            </div>
          </div>
        </div>
  
        <!-- Changes Summary Section -->
        <div v-if="showChanges" class="changes-summary">
          <h2 class="changes-summary__title">Muutokset</h2>
          
          <!-- Added Horses -->
          <div v-if="addedHorses.length > 0" class="changes-group">
            <h3 class="changes-group__title added">Lisättävät hevoset</h3>
            <ul class="changes-list">
              <li v-for="horse in addedHorses" :key="'add-' + horse.horse_id" class="changes-item added">
                <span class="changes-item__name">{{ horse.horse_name }}</span>
                <span class="changes-item__price">{{ horse.price !== undefined && horse.price !== null ? horse.price.toLocaleString() + ' €' : 'Ladataan...' }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Removed Horses -->
          <div v-if="removedHorses.length > 0" class="changes-group">
            <h3 class="changes-group__title removed">Poistettavat hevoset</h3>
            <ul class="changes-list">
              <li v-for="horse in removedHorses" :key="'remove-' + horse.horse_id" class="changes-item removed">
                <span class="changes-item__name">{{ horse.horse_name }}</span>
                <span class="changes-item__price">{{ horse.price !== undefined && horse.price !== null ? horse.price.toLocaleString() + ' €' : 'Ladataan...' }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Jockey Changes -->
          <div v-if="isJockeyChanged" class="changes-group">
            <h3 class="changes-group__title changed">Ohjastajan muutos</h3>
            <div class="changes-jockey">
              <div v-if="confirmedJockey" class="changes-item removed">
                <span class="changes-label">Poistetaan:</span>
                <span class="changes-item__name">{{ confirmedJockey.jockey_name }}</span>
                <span class="changes-item__price">{{ confirmedJockey.price !== undefined && confirmedJockey.price !== null ? confirmedJockey.price.toLocaleString() + ' €' : 'Ladataan...' }}</span>
              </div>
              <div v-if="pendingJockey" class="changes-item added">
                <span class="changes-label">Lisätään:</span>
                <span class="changes-item__name">{{ pendingJockey.jockey_name }}</span>
                <span class="changes-item__price">{{ pendingJockey.price !== undefined && pendingJockey.price !== null ? pendingJockey.price.toLocaleString() + ' €' : 'Ladataan...' }}</span>
              </div>
            </div>
          </div>
          
          <!-- Money Change Summary -->
          <div class="changes-money">
            <div class="changes-money__item">
              <span class="changes-money__label">Rahaa ennen muutoksia:</span>
              <span class="changes-money__value">{{ playerMoney.toLocaleString() }} €</span>
            </div>
            <div class="changes-money__item">
              <span class="changes-money__label">Rahaa muutosten jälkeen:</span>
              <span class="changes-money__value">{{ displayMoney.toLocaleString() }} €</span>
            </div>
            <div class="changes-money__item" :class="{'money-positive': displayMoney > playerMoney, 'money-negative': displayMoney < playerMoney}">
              <span class="changes-money__label">Muutos:</span>
              <span class="changes-money__value">{{ (displayMoney - playerMoney).toLocaleString() }} €</span>
            </div>
          </div>
        </div>

        <!-- Confirmed Changes Summary Section -->
        <div v-if="hasConfirmedChanges && confirmedChangesExist" class="changes-summary confirmed-changes">
          <h2 class="changes-summary__title">Vahvistetut muutokset</h2>
          
          <!-- Incoming Horses (in current but not in original) -->
          <div v-if="incomingHorses.length > 0" class="changes-group">
            <h3 class="changes-group__title added">Lisätyt hevoset</h3>
            <ul class="changes-list">
              <li v-for="horse in incomingHorses" :key="'incoming-' + horse.horse_id" class="changes-item added">
                <span class="changes-item__name">{{ horse.horse_name }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Outgoing Horses (in original but not in current) -->
          <div v-if="outgoingHorses.length > 0" class="changes-group">
            <h3 class="changes-group__title removed">Poistetut hevoset</h3>
            <ul class="changes-list">
              <li v-for="horse in outgoingHorses" :key="'outgoing-' + horse.horse_id" class="changes-item removed">
                <span class="changes-item__name">{{ horse.horse_name }}</span>
              </li>
            </ul>
          </div>
          
          <!-- Jockey Changes -->
          <div v-if="isConfirmedJockeyChanged" class="changes-group">
            <h3 class="changes-group__title changed">Ohjastajan muutos</h3>
            <div class="changes-jockey">
              <div v-if="originalJockey" class="changes-item removed">
                <span class="changes-label">Poistettu:</span>
                <span class="changes-item__name">{{ originalJockey.jockey_name }}</span>
              </div>
              <div v-if="currentJockey && currentJockey.jockey_id !== (originalJockey && originalJockey.jockey_id)" class="changes-item added">
                <span class="changes-label">Lisätty:</span>
                <span class="changes-item__name">{{ currentJockey.jockey_name }}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="confirmation-buttons mt-6 text-center">
          <template v-if="hasConfirmedChanges">
            <button @click="revertSelections" class="px-4 py-2 rounded" :disabled="changesDisabled" :class="{'disabled-button': changesDisabled}" :title="changesDisabled ? changesDisabledMessage : ''">Peru vahvistetut muutokset</button>
          </template>
          <template v-else-if="hasUnsavedChanges">
            <button @click="confirmChanges" class="px-4 py-2 rounded" 
              :disabled="isConfirming || changesDisabled || isTeamOverpriced" 
              :class="{'disabled-button': changesDisabled || isTeamOverpriced}" 
              :title="changesDisabled ? changesDisabledMessage : 
                     isTeamOverpriced ? 'Remmi on ylihintainen. Sinulla ei ole tarpeeksi rahaa.' : ''">
              {{ isConfirming ? 'Vahvistetaan...' : 'Vahvista muutokset' }}
            </button>
            <button @click="cancelUnsavedChanges" class="px-4 py-2 rounded">Peru muutokset</button>
            <p v-if="isTeamOverpriced" class="text-red-500 mt-2">Joukkue on ylihintainen. Valitse edullisempia valintoja.</p>
          </template>
          <p v-else class="italic">Ei tehtyjä vaihtoja tänään.</p>
        </div>
  
        <HorseSelectionModal
          :show="showHorseSelectionModal"
          :currently-selected-horse-ids="pendingHorses.map(h => h.horse_id)"
          :changing-horse-id="horseToChange ? horseToChange.id : null"
          :player-money="displayMoney"
          :changing-horse-price="horseToChange ? (pendingHorses.find(h => h.horse_id === horseToChange.id)?.price || 0) : 0"
          @close="closeHorseSelectionModal"
          @horse-selected="handleHorseSelected"
        />
  
        <JockeySelectionModal
          :show="showJockeySelectionModal"
          :current-jockey-id="pendingJockey ? pendingJockey.jockey_id : null"
          :player-money="displayMoney"
          :changing-jockey-price="pendingJockey ? pendingJockey.price || 0 : 0"
          @close="closeJockeySelectionModal"
          @jockey-selected="handleJockeySelected"
        />
  
        <Teleport to="body">
          <HorsePerformanceModal
              :show="isHorsePerformanceModalVisible"
              :horse-id="selectedHorseId"
              :horse-name="selectedHorseName"
              @close="closeHorsePerformanceModal"
          />
        </Teleport>
  
        <Teleport to="body">
          <JockeyPerformanceModal
              :show="isJockeyPerformanceModalVisible"
              :jockey-id="selectedJockeyId"
              :jockey-name="selectedJockeyName"
              @close="closeJockeyPerformanceModal"
          />
        </Teleport>

        <Teleport to="body">
          <TermsModal
              :show="showTermsModal"
              @close="showTermsModal = false"
          />
        </Teleport>
  
      </div>
      <div v-else class="text-center p-6 login-prompt">
        <p>Kirjaudu sisään hallinnoidaksesi valintojasi.</p>
      </div>
    </div>
  </template>
  
  <script>
  import { ref, onMounted, onUnmounted, inject, watch, computed } from 'vue';
  import axios from '../plugins/axios';
  
  // Import Modal Components
  import HorsePerformanceModal from './HorsePerformanceModal.vue';
  import JockeyPerformanceModal from './JockeyPerformanceModal.vue';
  import HorseSelectionModal from './HorseSelectionModal.vue'; // NEW
  import JockeySelectionModal from './JockeySelectionModal.vue'; // NEW
  import TermsModal from './TermsModal.vue'; // NEW
  import DailyPointsChart from './DailyPointsChart.vue';
  
  export default {
    name: 'OwnTeamPage',
    components: {
      HorsePerformanceModal,
      JockeyPerformanceModal,
      HorseSelectionModal, // NEW
      JockeySelectionModal, // NEW
      TermsModal, // NEW
      DailyPointsChart,
    },
    setup() {
      const isAuthenticated = inject('isAuthenticated');
      const racesOngoing = ref(false);
      const changesClosed = ref(false);
      const changeWindowReason = ref('open');
      const raceStatusMessage = ref('');
      const periodEnded = ref(false);
      const racesCompleted = ref(false);
  
      // --- Existing State Variables (some will be modified/removed) ---
      const pendingHorses = ref([]); // Array of { horse_id, horse_name, price }
      const pendingJockey = ref(null); // Object { jockey_id, jockey_name, price }
      const confirmedHorses = ref([]);
      const confirmedJockey = ref(null);
      const playerMoney = ref(0);
      const changesLeft = ref(0);
      const hasUnsavedChanges = ref(false);
      const hasConfirmedChanges = ref(false);
      // null = unknown (initial load), true/false after selections/profile response.
      // This avoids showing the premium upsell briefly to paid users.
      const isPaidUser = ref(null);
      const showPremiumUpgrade = ref(false);
      const isGeneratingInvoice = ref(false);
      const cancelRightWaived = ref(false);
      const termsAccepted = ref(false);
      const showTermsModal = ref(false);
      // Removed: showSelectionModal, selectionType, selectionList, sortOrder (moved to individual modals)
      // Removed: selectionIndex (will be handled differently)
      const totalPoints = ref(0);
      const pointsBreakdown = ref([]);
      const showPointsBreakdown = ref(false);
      const horseStarts = ref(0);
      const horseWins = ref(0);
      const horseLeadCount = ref(0);
      const horsePrizeSum = ref(0);
      const showChanges = ref(false); // Control visibility of changes summary
      const playerId = ref(null);
      const playerRank = ref(null);
      const initialMoney = ref(0); // Store the initial money value from the API
  
      // --- State Variables for Performance Modals (Existing) ---
      const isHorsePerformanceModalVisible = ref(false);
      const selectedHorseId = ref(null);
      const selectedHorseName = ref('');
      const isJockeyPerformanceModalVisible = ref(false);
      const selectedJockeyId = ref(null);
      const selectedJockeyName = ref('');
  
      // --- *** NEW State Variables for SELECTION Modals *** ---
      const showHorseSelectionModal = ref(false);
      const showJockeySelectionModal = ref(false);
      const horseChangeIndex = ref(null); // To track which horse slot is being changed
      const horseToChange = ref(null); // To pass the ID of the horse being changed to the modal
      
      // --- Variables for price fetching ---
      const isFetchingPrices = ref(false);
  
      // --- Fetch Player Rank (Existing - Unchanged) ---
      // Function to fetch horse price
      const fetchHorsePrice = async (horseId, index) => {
        try {
          // Use the new dedicated endpoint for fetching horse price by ID
          const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/horse_price/${horseId}`);
          const horse = response.data;
          if (horse && horse.price !== undefined) {
            // Update the horse price in the pendingHorses array
            pendingHorses.value[index].price = horse.price;
            // Also update in confirmedHorses if it exists there
            const confirmedIndex = confirmedHorses.value.findIndex(h => h.horse_id === horseId);
            if (confirmedIndex !== -1) {
              confirmedHorses.value[confirmedIndex].price = horse.price;
            }
          }
        } catch (error) {
          console.error(`Error fetching price for horse ${horseId}:`, error);
        }
      };

      // Function to fetch jockey price
      const fetchJockeyPrice = async (jockeyId) => {
        try {
          // Use the new dedicated endpoint for fetching jockey price by ID
          const response = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/jockey_price/${jockeyId}`);
          const jockey = response.data;
          if (jockey && jockey.price !== undefined) {
            // Update the jockey price
            if (pendingJockey.value && pendingJockey.value.jockey_id === jockeyId) {
              pendingJockey.value.price = jockey.price;
            }
            // Also update in confirmedJockey if it exists
            if (confirmedJockey.value && confirmedJockey.value.jockey_id === jockeyId) {
              confirmedJockey.value.price = jockey.price;
            }
          }
        } catch (error) {
          console.error(`Error fetching price for jockey ${jockeyId}:`, error);
        }
      };

      const fetchPlayerRank = async () => {
          if (!isAuthenticated.value) {
              playerRank.value = null;
              return;
          }
          try {
              let currentPId = playerId.value;
              if (!currentPId) {
                  const meResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/me`);
                  if (meResponse.data && meResponse.data.PlayerID) {
                      playerId.value = meResponse.data.PlayerID;
                      currentPId = playerId.value;
                  } else {
                      playerRank.value = '-'; return;
                  }
              }
              if (currentPId) {
                  const rankResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/player_rank/${currentPId}`);
                  if (rankResponse.data && rankResponse.data.rank !== undefined) {
                      playerRank.value = rankResponse.data.rank;
                  } else {
                      playerRank.value = '-';
                  }
              } else {
                  playerRank.value = '-';
              }
          } catch (error) {
              console.error('Error fetching player ID or rank:', error.response?.data || error.message);
              // New players can be absent from the last published snapshot.
              playerRank.value = '-';
          }
      };
  
      // Check race status and game period
      const checkRaceStatus = async () => {
        try {
          // Get race status
          const raceResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/race-status`);
          if (raceResponse.data) {
            racesOngoing.value = Boolean(raceResponse.data.racesOngoing);
            changesClosed.value = Boolean(
              raceResponse.data?.changeWindow?.isClosed ?? raceResponse.data?.changesClosed ?? raceResponse.data?.racesOngoing
            );
            changeWindowReason.value = raceResponse.data?.changeWindow?.reason || (changesClosed.value ? 'races_ongoing' : 'open');
            raceStatusMessage.value = raceResponse.data.message;
            console.log("Race status:", raceResponse.data);
          }
          
          // Get game period data
          const periodResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/game-period`);
          if (periodResponse.data && periodResponse.data.endDate) {
            console.log("Received game period data:", periodResponse.data);
            
            const endDate = new Date(periodResponse.data.endDate);
            const today = new Date();
            
            // Check if period has ended
            periodEnded.value = today > endDate;
            
            // Check if it's the last day of the period
            const isLastDay = today.toDateString() === endDate.toDateString();
            
            racesCompleted.value = changeWindowReason.value === 'last_race_day_closed'
              || (isLastDay && raceResponse.data.status === 'completed');
            
            console.log(`Period ended: ${periodEnded.value}, Last day: ${isLastDay}, Races completed: ${racesCompleted.value}`);
          }
        } catch (error) {
          console.error("Error checking race status or game period:", error);
          racesOngoing.value = false;
          changesClosed.value = false;
          changeWindowReason.value = 'unknown';
          raceStatusMessage.value = "Virhe kilpailutilan tarkistamisessa";
          periodEnded.value = false;
          racesCompleted.value = false;
        }
      };
      
      // --- Fetch Initial Selections and Player Data (Existing - Unchanged) ---
      const fetchSelections = () => {
        if (!isAuthenticated.value) {
          // Clear all states
          pendingHorses.value = []; pendingJockey.value = null;
          confirmedHorses.value = []; confirmedJockey.value = null;
          originalHorses.value = []; currentHorses.value = [];
          originalJockey.value = null; currentJockey.value = null;
          playerMoney.value = 0; changesLeft.value = 0; totalPoints.value = 0;
          pointsBreakdown.value = []; hasUnsavedChanges.value = false; hasConfirmedChanges.value = false;
          horseStarts.value = 0; horseWins.value = 0; horseLeadCount.value = 0; horsePrizeSum.value = 0;
          isPaidUser.value = null; // Clear paid status
          showChanges.value = false; // Hide changes summary
          closeHorsePerformanceModal(); closeJockeyPerformanceModal();
          closeHorseSelectionModal(); closeJockeySelectionModal(); // Close new modals too
          playerId.value = null; playerRank.value = null;
          return;
        }
        fetchPlayerRank();
        // Mark paid status as unknown until we have a response from the backend.
        isPaidUser.value = null;
        axios.get(`${process.env.VUE_APP_API_BASE_URL}/player_selections`)
          .then(response => {
            const {
              originalHorses: apiOriginalHorses = [], 
              currentHorses: apiCurrentHorses = [], 
              originalJockey: apiOriginalJockey = null, 
              currentJockey: apiCurrentJockey = null,
              playerMoney: money = 0, changesLeft: changes = 0,
              totalPoints: fetchedTotalPoints = 0, pointsBreakdown: fetchedPointsBreakdown = [],
              horseStarts: fetchedHorseStarts = 0,
              horseWins: fetchedHorseWins = 0,
              horseLeadCount: fetchedHorseLeadCount = 0,
              horsePrizeSum: fetchedHorsePrizeSum = 0,
            } = response.data;
            const paid = response.data?.isPaid;
  
            // Store original and current selections from API
            originalHorses.value = JSON.parse(JSON.stringify(apiOriginalHorses || []));
            currentHorses.value = JSON.parse(JSON.stringify(apiCurrentHorses || []));
            originalJockey.value = apiOriginalJockey ? JSON.parse(JSON.stringify(apiOriginalJockey)) : null;
            currentJockey.value = apiCurrentJockey ? JSON.parse(JSON.stringify(apiCurrentJockey)) : null;
            
            const initialHorses = (Array.isArray(apiCurrentHorses) && apiCurrentHorses.length > 0) ? apiCurrentHorses : apiOriginalHorses;
            const initialJockey = apiCurrentJockey !== null ? apiCurrentJockey : apiOriginalJockey;
            
            pendingHorses.value = JSON.parse(JSON.stringify(initialHorses || []));
            pendingJockey.value = initialJockey ? JSON.parse(JSON.stringify(initialJockey)) : null;
            confirmedHorses.value = JSON.parse(JSON.stringify(pendingHorses.value));
            confirmedJockey.value = pendingJockey.value ? JSON.parse(JSON.stringify(pendingJockey.value)) : null;
            hasConfirmedChanges.value = (Array.isArray(apiCurrentHorses) && apiCurrentHorses.length > 0);
            playerMoney.value = money; 
            initialMoney.value = money; // Store the initial money value
            changesLeft.value = changes;
            totalPoints.value = fetchedTotalPoints;
            pointsBreakdown.value = (fetchedPointsBreakdown || []).sort((a, b) => (b.date || '').localeCompare(a.date || ''));
            horseStarts.value = fetchedHorseStarts;
            horseWins.value = fetchedHorseWins;
            horseLeadCount.value = fetchedHorseLeadCount;
            horsePrizeSum.value = fetchedHorsePrizeSum;
            isPaidUser.value = (typeof paid === 'boolean') ? paid : null;
            hasUnsavedChanges.value = false;
            showChanges.value = false; // Reset changes summary visibility on fetch
            
            // Check if we need to fetch prices for horses or jockey
            const needHorsePrices = pendingHorses.value.some(horse => 
              horse.price === undefined || horse.price === null);
            const needJockeyPrice = pendingJockey.value && 
              (pendingJockey.value.price === undefined || pendingJockey.value.price === null);
            
            if (needHorsePrices || needJockeyPrice) {
              isFetchingPrices.value = true;
              
              // Fetch prices for horses that don't have them
              if (needHorsePrices) {
                pendingHorses.value.forEach((horse, index) => {
                  if (horse.horse_id && (horse.price === undefined || horse.price === null)) {
                    fetchHorsePrice(horse.horse_id, index);
                  }
                });
              }
              
              // Fetch price for jockey if needed
              if (needJockeyPrice && pendingJockey.value.jockey_id) {
                fetchJockeyPrice(pendingJockey.value.jockey_id);
              }
              
              isFetchingPrices.value = false;
            }
          })
          .catch(error => {
            console.error('Error fetching selections:', error.response?.data || error.message);
            // Keep paid status unknown on error so we don't accidentally show the premium upsell to paid users.
            isPaidUser.value = null;
            alert("Pelaajan valintoja ei voitu ladata. Yritä myöhemmin uudelleen.");
          });
      };
  
      // --- Toggle Points Breakdown (Existing - Unchanged) ---
      const togglePointsBreakdown = () => {
        showPointsBreakdown.value = !showPointsBreakdown.value;
      };

      // Add: Helper to determine if a date separator should be shown
      const shouldShowDateSeparator = (index) => {
        if (index === 0) return false;
        if (!pointsBreakdown.value.length || index >= pointsBreakdown.value.length) return false;
        const currentItem = pointsBreakdown.value[index];
        const previousItem = pointsBreakdown.value[index - 1];
        return currentItem.date !== previousItem.date;
      };

      // Helper: is this breakdown item a clickable link?
      const isBreakdownLink = (item) => {
        return (item.type === 'Horse' || item.type === 'Jockey') && !!item.selectionKey;
      };

      // Helper: extract id from selectionKey
      const extractIdFromKey = (key) => {
        if (!key || typeof key !== 'string') return null;
        const parts = key.split('#');
        return parts.length > 0 ? parts[parts.length - 1] : null;
      };

      // Handler for clicking horse/jockey name in breakdown
      const handleBreakdownClick = (item) => {
        if (!isBreakdownLink(item)) return;
        const id = extractIdFromKey(item.selectionKey);
        if (!id) return;
        if (item.type === 'Horse') {
          openHorsePerformanceModal(id, item.name);
        } else if (item.type === 'Jockey') {
          openJockeyPerformanceModal(id, item.name);
        }
      };
  
      // --- *** NEW: Horse Selection Modal Triggers & Handler *** ---
      const triggerAddHorse = async () => {
        if (changesDisabled.value) {
          alert(changesDisabledMessage.value);
          return;
        }
        
        if (hasConfirmedChanges.value) {
          alert('Peru vahvistetut muutokset ensin ennen uusien muutosten tekemistä.');
          return;
        }
        
        // Check if player has selections and no changes left
        // Allow free changes before game period starts
        const beforeGamePeriod = await isBeforeGamePeriod();
        if (hasAnySelections.value && changesLeft.value <= 0 && !beforeGamePeriod) {
          alert('Ei vaihtoja jäljellä tälle jaksolle.');
          return;
        }
        
        if (pendingHorses.value.length >= 5) {
          alert('Et voi valita enempää kuin 5 hevosta.');
          return;
        }
        horseChangeIndex.value = null; // Indicates adding a new horse
        horseToChange.value = null;
        showHorseSelectionModal.value = true;
      };
  
      const triggerChangeHorse = async (index) => {
        if (changesDisabled.value) {
          alert(changesDisabledMessage.value);
          return;
        }
        
        if (hasConfirmedChanges.value) {
          alert('Peru vahvistetut muutokset ensin ennen uusien muutosten tekemistä.');
          return;
        }
        
        // Check if player has selections and no changes left
        // Allow free changes before game period starts
        const beforeGamePeriod = await isBeforeGamePeriod();
        if (hasAnySelections.value && changesLeft.value <= 0 && !beforeGamePeriod) {
          alert('Ei vaihtoja jäljellä tälle jaksolle.');
          return;
        }
        
        horseChangeIndex.value = index;
        horseToChange.value = { id: pendingHorses.value[index].horse_id }; // Pass current horse ID
        showHorseSelectionModal.value = true;
      };
  
      const handleHorseSelected = (selectedHorse) => {
        // Double-check that we don't have confirmed changes
        if (hasConfirmedChanges.value) {
          closeHorseSelectionModal();
          return;
        }
        
        // Ensure selectedHorse has all necessary properties (id, name, price)
        const newHorseEntry = {
          horse_id: selectedHorse.id,
          horse_name: selectedHorse.name,
          price: selectedHorse.price !== undefined ? selectedHorse.price : null,
          racing: selectedHorse.racing || 'no',
          upcomingStarts: Array.isArray(selectedHorse.upcomingStarts) ? selectedHorse.upcomingStarts : []
        };
  
        if (horseChangeIndex.value !== null) { // Changing an existing horse
          pendingHorses.value.splice(horseChangeIndex.value, 1, newHorseEntry);
        } else { // Adding a new horse
          if (pendingHorses.value.length < 5) {
            pendingHorses.value.push(newHorseEntry);
          } else {
             // Should be caught by triggerAddHorse, but as a safeguard
            alert("Et voi lisätä enempää kuin 5 hevosta.");
          }
        }
        closeHorseSelectionModal();
        checkUnsavedChanges();
      };
  
      const closeHorseSelectionModal = () => {
        showHorseSelectionModal.value = false;
        horseChangeIndex.value = null;
        horseToChange.value = null;
      };
  
      // --- *** NEW: Jockey Selection Modal Triggers & Handler *** ---
      const triggerSelectJockey = async () => { // For adding a new jockey
        if (changesDisabled.value) {
          alert(changesDisabledMessage.value);
          return;
        }
        
        if (hasConfirmedChanges.value) {
          alert('Peru vahvistetut muutokset ensin ennen uusien muutosten tekemistä.');
          return;
        }
        
        // Check if player has selections and no changes left
        // Allow free changes before game period starts
        const beforeGamePeriod = await isBeforeGamePeriod();
        if (hasAnySelections.value && changesLeft.value <= 0 && !beforeGamePeriod) {
          alert('Ei vaihtoja jäljellä tälle jaksolle.');
          return;
        }
        
        showJockeySelectionModal.value = true;
      };
  
      const triggerChangeJockey = async () => { // For changing an existing jockey
        if (changesDisabled.value) {
          alert(changesDisabledMessage.value);
          return;
        }
        
        if (hasConfirmedChanges.value) {
          alert('Peru vahvistetut muutokset ensin ennen uusien muutosten tekemistä.');
          return;
        }
        
        // Check if player has selections and no changes left
        // Allow free changes before game period starts
        const beforeGamePeriod = await isBeforeGamePeriod();
        if (hasAnySelections.value && changesLeft.value <= 0 && !beforeGamePeriod) {
          alert('Ei vaihtoja jäljellä tälle jaksolle.');
          return;
        }
        
        showJockeySelectionModal.value = true;
      };
  
      const handleJockeySelected = (selectedJockey) => {
        // Double-check that we don't have confirmed changes
        if (hasConfirmedChanges.value) {
          closeJockeySelectionModal();
          return;
        }
        
        pendingJockey.value = {
          jockey_id: selectedJockey.id,
          jockey_name: selectedJockey.name,
          price: selectedJockey.price !== undefined ? selectedJockey.price : null
        };
        closeJockeySelectionModal();
        checkUnsavedChanges();
      };
  
      const closeJockeySelectionModal = () => {
        showJockeySelectionModal.value = false;
      };
  
  
      // --- Helper to Check for Unsaved Changes ---
      // Function to check if we're before the game period (matches backend logic exactly)
      const isBeforeGamePeriod = async () => {
        try {
          // Get game period start date from the same API that App.vue uses
          const gamePeriodResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/game-period`);
          if (!gamePeriodResponse.data || !gamePeriodResponse.data.startDate) {
            console.error('Could not get game period start date');
            return false; // Default to not allowing free changes if error
          }
          
          const gameStartDateStr = gamePeriodResponse.data.startDate;
          const gameStartDate = new Date(gameStartDateStr);
          const currentDate = new Date();
          
          // Remove time component for date comparison (same as backend)
          const currentDateOnly = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          const gameStartDateOnly = new Date(gameStartDate.getFullYear(), gameStartDate.getMonth(), gameStartDate.getDate());
          
          // Case 1: Current date is before game period start date
          if (currentDateOnly < gameStartDateOnly) {
            console.log('Current date is before game period start date. Changes are free.');
            return true;
          }
          
          // Case 2: Current date is the first day of game period - check race status
          if (currentDateOnly.getTime() === gameStartDateOnly.getTime()) {
            // Get race status for today
            const raceStatusResponse = await axios.get(`${process.env.VUE_APP_API_BASE_URL}/race-status`);
            
            // Check if the race status is for today's date
            const todayStr = currentDate.toISOString().split('T')[0]; // Get YYYY-MM-DD format
            const raceStatusDate = raceStatusResponse.data?.date;
            
            // If race status is for today and status is upcoming, allow changes
            if (raceStatusResponse.data && 
                raceStatusDate === todayStr && 
                raceStatusResponse.data.status === 'upcoming') {
              console.log('First day of game period, today\'s races upcoming. Changes are free.');
              return true;
            } 
            // If race status is for yesterday or status is not upcoming, but it's still the first day, allow changes
            else if (raceStatusDate !== todayStr) {
              console.log('First day of game period, no race schedule for today yet. Changes are free.');
              return true;
            }
            else {
              console.log('First day of game period, races started. Changes restricted.');
              return false;
            }
          }
          
          // Case 3: After game period start date
          return false;
          
        } catch (error) {
          console.error('Error checking game period:', error);
          // Default to not allowing free changes if there's an error
          return false;
        }
      };

      const checkUnsavedChanges = () => {
        const pendingHorsesStr = JSON.stringify(pendingHorses.value.map(h => h?.horse_id).sort());
        const confirmedHorsesStr = JSON.stringify(confirmedHorses.value.map(h => h?.horse_id).sort());
        const pendingJockeyStr = JSON.stringify(pendingJockey.value?.jockey_id || null);
        const confirmedJockeyStr = JSON.stringify(confirmedJockey.value?.jockey_id || null);
        const previousUnsavedChanges = hasUnsavedChanges.value;
        hasUnsavedChanges.value = pendingHorsesStr !== confirmedHorsesStr || pendingJockeyStr !== confirmedJockeyStr;
        
        // Show changes summary if there are unsaved changes, hide if no changes
        if (hasUnsavedChanges.value) {
          showChanges.value = true;
        } else if (previousUnsavedChanges !== hasUnsavedChanges.value) {
          // Only hide if the state actually changed (to avoid hiding when already hidden)
          showChanges.value = false;
        }
      };
      
      // --- Computed property for dynamic money calculation ---
      const displayMoney = computed(() => {
        // Start with the initial money value
        let currentMoney = initialMoney.value;
        
        // If there are no unsaved changes, just return the player's money
        if (!hasUnsavedChanges.value) {
          return playerMoney.value;
        }
        
        // Calculate the cost of confirmed selections
        let confirmedCost = 0;
        
        // Add up the prices of confirmed horses
        confirmedHorses.value.forEach(horse => {
          if (horse.price !== undefined && horse.price !== null) {
            confirmedCost += horse.price;
          }
        });
        
        // Add the price of confirmed jockey
        if (confirmedJockey.value && confirmedJockey.value.price !== undefined && confirmedJockey.value.price !== null) {
          confirmedCost += confirmedJockey.value.price;
        }
        
        // Calculate the cost of pending selections
        let pendingCost = 0;
        
        // Add up the prices of pending horses
        pendingHorses.value.forEach(horse => {
          if (horse.price !== undefined && horse.price !== null) {
            pendingCost += horse.price;
          }
        });
        
        // Add the price of pending jockey
        if (pendingJockey.value && pendingJockey.value.price !== undefined && pendingJockey.value.price !== null) {
          pendingCost += pendingJockey.value.price;
        }
        
        // Calculate the difference and adjust the current money
        currentMoney = initialMoney.value - pendingCost + confirmedCost;
        
        return currentMoney;
      });
      
      // --- Computed property to check if team is overpriced ---
      const isTeamOverpriced = computed(() => {
        return displayMoney.value < 0;
      });
      
      // --- Computed properties for tracking changes ---
      
      // Horses being added (in pending but not in confirmed)
      const addedHorses = computed(() => {
        if (!hasUnsavedChanges.value) return [];
        
        return pendingHorses.value.filter(pendingHorse => 
          !confirmedHorses.value.some(confirmedHorse => 
            confirmedHorse.horse_id === pendingHorse.horse_id
          )
        );
      });
      
      // Horses being removed (in confirmed but not in pending)
      const removedHorses = computed(() => {
        if (!hasUnsavedChanges.value) return [];
        
        return confirmedHorses.value.filter(confirmedHorse => 
          !pendingHorses.value.some(pendingHorse => 
            pendingHorse.horse_id === confirmedHorse.horse_id
          )
        );
      });
      
      // Check if jockey is being changed
      const isJockeyChanged = computed(() => {
        if (!hasUnsavedChanges.value) return false;
        
        // If one is null and the other isn't, jockey is being added or removed
        if ((!confirmedJockey.value && pendingJockey.value) || 
            (confirmedJockey.value && !pendingJockey.value)) {
          return true;
        }
        
        // If both exist, check if they're different
        if (confirmedJockey.value && pendingJockey.value) {
          return confirmedJockey.value.jockey_id !== pendingJockey.value.jockey_id;
        }
        
        return false;
      });
      
      // --- Computed properties for tracking confirmed changes (after confirmation) ---
      
      // State variables for original and current selections from API
      const originalHorses = ref([]);
      const currentHorses = ref([]);
      const originalJockey = ref(null);
      const currentJockey = ref(null);
      
      // Check if there are any confirmed changes to display
      const confirmedChangesExist = computed(() => {
        return incomingHorses.value.length > 0 || 
               outgoingHorses.value.length > 0 || 
               isConfirmedJockeyChanged.value;
      });
      
      // Horses that were added (in current but not in original)
      const incomingHorses = computed(() => {
        if (!hasConfirmedChanges.value) return [];
        
        return currentHorses.value.filter(currentHorse => 
          !originalHorses.value.some(originalHorse => 
            originalHorse.horse_id === currentHorse.horse_id
          )
        );
      });
      
      // Horses that were removed (in original but not in current)
      const outgoingHorses = computed(() => {
        if (!hasConfirmedChanges.value) return [];
        
        return originalHorses.value.filter(originalHorse => 
          !currentHorses.value.some(currentHorse => 
            currentHorse.horse_id === originalHorse.horse_id
          )
        );
      });
      
      // Check if jockey was changed in confirmed selections
      const isConfirmedJockeyChanged = computed(() => {
        if (!hasConfirmedChanges.value) return false;
        
        // If one is null and the other isn't, jockey was added or removed
        if ((!originalJockey.value && currentJockey.value) || 
            (originalJockey.value && !currentJockey.value)) {
          return true;
        }
        
        // If both exist, check if they're different
        if (originalJockey.value && currentJockey.value) {
          return originalJockey.value.jockey_id !== currentJockey.value.jockey_id;
        }
        
        return false;
      });
  
      // --- Confirm/Cancel/Revert Actions (Existing - Unchanged) ---
      const confirmChanges = async () => {
        if (isConfirming.value) return; // Prevent double-clicking
        
        if (pendingHorses.value.filter(h => h && h.horse_id !== undefined).length !== 5) {
          alert('Sinun tulee valita täsmälleen 5 hevosta ennen vahvistamista.'); return;
        }
        if (!pendingJockey.value || pendingJockey.value.jockey_id === undefined) {
          alert('Sinun tulee valita ohjastaja ennen vahvistamista.'); return;
        }
        if (isTeamOverpriced.value) {
          alert('Joukkue on ylihintainen. Sinulla ei ole tarpeeksi rahaa.'); return;
        }
        
        isConfirming.value = true;
        
        try {
          const data = {
            horses: pendingHorses.value.map(h => ({ horse_id: h.horse_id, horse_name: h.horse_name, price: h.price })),
            jockey: { jockey_id: pendingJockey.value.jockey_id, jockey_name: pendingJockey.value.jockey_name, price: pendingJockey.value.price },
          };
          
          await axios.post(`${process.env.VUE_APP_API_BASE_URL}/player_selections`, data);
          fetchSelections();
        } catch (error) {
          alert(`Virhe valintojen päivittämisessä: ${error.response?.data?.message || error.message}`);
        } finally {
          isConfirming.value = false;
        }
      };
  
      const cancelUnsavedChanges = () => {
        pendingHorses.value = JSON.parse(JSON.stringify(confirmedHorses.value));
        pendingJockey.value = confirmedJockey.value ? JSON.parse(JSON.stringify(confirmedJockey.value)) : null;
        hasUnsavedChanges.value = false;
        showChanges.value = false; // Hide changes summary when canceling
      };
  
      const revertSelections = () => {
        axios.delete(`${process.env.VUE_APP_API_BASE_URL}/player_selections`)
          .then(() => { 
            fetchSelections(); 
            showChanges.value = false; // Hide changes summary after reverting
          })
          .catch(error => { alert(`Virhe valintojen peruuttamisessa: ${error.response?.data?.message || error.message}`); });
      };
  
      // --- Premium Upgrade Functions ---
      const generateInvoice = async () => {
        if (isGeneratingInvoice.value) return;
        
        isGeneratingInvoice.value = true;
        try {
          const response = await axios.post(`${process.env.VUE_APP_API_BASE_URL}/generate-invoice`, {});
          
          if (response.status === 200) {
            alert(`Lasku lähetetty sähköpostiosoitteeseesi! Laskun numero: ${response.data.invoice_number}. Summa: ${response.data.amount}€. Premium-käyttöoikeus on nyt aktivoitu.`);
            // Update paid status immediately
            isPaidUser.value = true;
            // Refresh selections to get updated data
            fetchSelections();
          } else {
            throw new Error(response.data?.error || 'Laskun luonti epäonnistui');
          }
        } catch (error) {
          console.error('Error generating invoice:', error);
          if (error.response?.status === 403) {
            alert('Sinulla on jo Premium-käyttöoikeus. Laskua ei tarvita.');
          } else {
            alert(`Virhe laskun luonnissa: ${error.response?.data?.error || error.message}`);
          }
        } finally {
          isGeneratingInvoice.value = false;
          showPremiumUpgrade.value = false;
        }
      };

      const confirmPremiumUpgrade = () => {
        showPremiumUpgrade.value = true;
      };

      const cancelPremiumUpgrade = () => {
        showPremiumUpgrade.value = false;
      };

      // --- End Premium Upgrade Functions ---
      const openHorsePerformanceModal = (horseIdVal, horseNameVal) => {
        if (!horseIdVal) return;
        selectedHorseId.value = horseIdVal; selectedHorseName.value = horseNameVal || 'Selected Horse';
        isHorsePerformanceModalVisible.value = true; closeJockeyPerformanceModal();
      };

      const formatUpcomingStarts = (horse) => {
        const starts = Array.isArray(horse?.upcomingStarts) ? horse.upcomingStarts : [];
        if (starts.length === 0) {
          return '';
        }
        return starts
          .map(start => {
            const date = start.displayDate || start.date || '';
            const venue = start.venue || '';
            const raceNumber = start.raceNumber ? `/${start.raceNumber}` : '';
            return [date, `${venue}${raceNumber}`.trim()].filter(Boolean).join(' ');
          })
          .filter(Boolean)
          .join(', ');
      };

      const closeHorsePerformanceModal = () => {
        isHorsePerformanceModalVisible.value = false; selectedHorseId.value = null; selectedHorseName.value = '';
      };
      const openJockeyPerformanceModal = (jockeyIdVal, jockeyNameVal) => {
        if (!jockeyIdVal) return;
        selectedJockeyId.value = jockeyIdVal; selectedJockeyName.value = jockeyNameVal || 'Selected Jockey';
        isJockeyPerformanceModalVisible.value = true; closeHorsePerformanceModal();
      };
      const closeJockeyPerformanceModal = () => {
        isJockeyPerformanceModalVisible.value = false; selectedJockeyId.value = null; selectedJockeyName.value = '';
      };
  
      // --- Lifecycle Hook (Modified to ensure prices are fetched) ---
      onMounted(() => {
        if (isAuthenticated.value) { 
          fetchSelections();
          checkRaceStatus();
          updateGamePeriodStatus(); // Update game period status on mount
        }
        
        // Set up interval to check race status every 5 minutes
        const raceStatusInterval = setInterval(() => {
          checkRaceStatus();
          updateGamePeriodStatus(); // Also update game period status
        }, 300000);
        
        // Clean up interval on component unmount
        onUnmounted(() => {
          clearInterval(raceStatusInterval);
        });
        
        // Add a debug log after a short delay to see the status
        setTimeout(() => {
          console.log("Debug status after mount:", {
            racesOngoing: racesOngoing.value,
            periodEnded: periodEnded.value,
            racesCompleted: racesCompleted.value,
            changesDisabled: changesDisabled.value
          });
        }, 2000);
      });
  
      // --- Watcher for Authentication Changes (Modified to close new modals) ---
      watch(isAuthenticated, (newValue, oldValue) => {
        if (newValue === true && oldValue === false) {
          fetchSelections();
        } else if (newValue === false) {
          fetchSelections(); // Clears states
          // ensure all modals are closed
          closeHorsePerformanceModal();
          closeJockeyPerformanceModal();
          closeHorseSelectionModal(); // NEW
          closeJockeySelectionModal(); // NEW
        }
      });
  
      // Computed property to check if player has any selections (initial or current)
      const hasAnySelections = computed(() => {
        const hasHorses = (originalHorses.value && originalHorses.value.length > 0) || 
                         (currentHorses.value && currentHorses.value.length > 0);
        const hasJockey = originalJockey.value !== null || currentJockey.value !== null;
        return hasHorses || hasJockey;
      });

      // Computed property to check if changes are disabled
      const changesDisabled = computed(() => {
        const result = changesClosed.value || periodEnded.value || racesCompleted.value;
        console.log(
          `Changes disabled: ${result} `
          + `(changesClosed: ${changesClosed.value}, racesOngoing: ${racesOngoing.value}, `
          + `periodEnded: ${periodEnded.value}, racesCompleted: ${racesCompleted.value})`
        );
        return result;
      });

      // Reactive ref to track if we're before game period
      const beforeGamePeriod = ref(false);
      
      // Function to update the beforeGamePeriod status
      const updateGamePeriodStatus = async () => {
        beforeGamePeriod.value = await isBeforeGamePeriod();
      };

      // Computed property to check if selection buttons should be disabled
      const selectionButtonsDisabled = computed(() => {
        const hasSelections = hasAnySelections.value;
        const confirmed = hasConfirmedChanges.value;
        const disabled = changesDisabled.value;
        const noChanges = changesLeft.value <= 0;
        const beforePeriod = beforeGamePeriod.value;
        
        console.log('selectionButtonsDisabled computed:', {
          hasSelections,
          confirmed,
          disabled,
          noChanges,
          beforePeriod,
          changesLeft: changesLeft.value
        });
        
        // If player has no selections yet, allow them to create initial team (doesn't consume changes)
        if (!hasSelections) {
          const result = disabled;
          console.log('No selections, returning:', result);
          return result; // Only disabled if races are ongoing/completed
        }
        // If player has selections, require changes left (unless before game period)
        const result = confirmed || disabled || (noChanges && !beforePeriod);
        console.log('Has selections, returning:', result);
        return result;
      });

      // Computed property to check if there are unconfirmed changes
      const hasUnconfirmedChanges = computed(() => {
        return hasUnsavedChanges.value && !hasConfirmedChanges.value;
      });

      // State for tracking confirmation process
      const isConfirming = ref(false);
      
      // Computed property for the tooltip message when changes are disabled
      const changesDisabledMessage = computed(() => {
        if (periodEnded.value) {
          return 'Muutokset eivät ole sallittuja, koska jakso on päättynyt';
        } else if (changeWindowReason.value === 'last_race_day_closed' || racesCompleted.value) {
          return 'Muutokset eivät ole sallittuja, koska kauden viimeinen kilpailupäivä on päättynyt';
        } else if (changeWindowReason.value === 'awaiting_results') {
          return 'Muutokset eivät ole sallittuja, ennen kuin päivän tulokset on käsitelty';
        } else if (racesOngoing.value || changesClosed.value) {
          return 'Muutokset eivät ole sallittuja kilpailujen aikana';
        }
        return '';
      });
      
      // Debug computed property to help troubleshoot
      const debugStatus = computed(() => {
        return {
          changesClosed: changesClosed.value,
          changeWindowReason: changeWindowReason.value,
          racesOngoing: racesOngoing.value,
          periodEnded: periodEnded.value,
          racesCompleted: racesCompleted.value,
          changesDisabled: changesDisabled.value
        };
      });

      return {
        isAuthenticated, pendingHorses, pendingJockey, playerMoney, changesLeft,
        hasUnsavedChanges, hasConfirmedChanges, totalPoints, pointsBreakdown, showPointsBreakdown,
        horseStarts, horseWins, horseLeadCount, horsePrizeSum,
        playerRank, isFetchingPrices, displayMoney, showChanges, isTeamOverpriced,
        addedHorses, removedHorses, isJockeyChanged, confirmedJockey,
        confirmChanges, cancelUnsavedChanges, revertSelections, togglePointsBreakdown,
        racesOngoing, raceStatusMessage, changesDisabled, changesDisabledMessage,
        hasAnySelections, selectionButtonsDisabled, hasUnconfirmedChanges, isConfirming, // Add new computed properties
        periodEnded, racesCompleted, debugStatus, isBeforeGamePeriod, beforeGamePeriod, updateGamePeriodStatus, // Add new functions and refs
  
        // Performance Modals
        isHorsePerformanceModalVisible, selectedHorseId, selectedHorseName,
        openHorsePerformanceModal, closeHorsePerformanceModal, formatUpcomingStarts,
        isJockeyPerformanceModalVisible, selectedJockeyId, selectedJockeyName,
        openJockeyPerformanceModal, closeJockeyPerformanceModal,
  
        // NEW Selection Modals & Triggers
        showHorseSelectionModal, triggerAddHorse, triggerChangeHorse, handleHorseSelected, closeHorseSelectionModal,
        showJockeySelectionModal, triggerSelectJockey, triggerChangeJockey, handleJockeySelected, closeJockeySelectionModal,
        horseToChange, // for the prop :changing-horse-id
        shouldShowDateSeparator, // NEW
        isBreakdownLink,
        handleBreakdownClick,
        
        // Confirmed changes section
        originalHorses, currentHorses, originalJockey, currentJockey,
        incomingHorses, outgoingHorses, isConfirmedJockeyChanged, confirmedChangesExist,
        
        // Premium upgrade functions
        isPaidUser, showPremiumUpgrade, isGeneratingInvoice,
        confirmPremiumUpgrade, cancelPremiumUpgrade, generateInvoice,
        cancelRightWaived, termsAccepted, showTermsModal,
      };
    },
  };
  </script>

<style scoped>
  /* Import base styles (Existing) */
  @import '../assets/styles.css';

  /* Confirmation Buttons Styling */
  .confirmation-buttons {
    margin-top: 2rem;
    display: flex;
    justify-content: center;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .confirmation-buttons button {
    padding: 0.75rem 1.5rem;
    cursor: pointer;
    font-weight: 600;
    transition: all 0.3s ease;
    border: none;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  .confirmation-buttons button:first-of-type {
    background-color: #64ffda;
    color: #0a192f;
  }
  .confirmation-buttons button:first-of-type:hover {
    background-color: #52d6c0;
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  .confirmation-buttons button:last-of-type {
    background-color: rgba(255, 107, 107, 0.1);
    color: #ff6b6b;
    border: 1px solid #ff6b6b;
  }
  .confirmation-buttons button:last-of-type:hover {
    background-color: rgba(255, 107, 107, 0.2);
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }
  .confirmation-buttons p {
    color: #8892b0;
    font-style: italic;
  }
  
  /* Changes Summary Styling */
  .changes-summary {
    background-color: #112240;
    border: 1px solid #1e3a5f;
    border-radius: 12px;
    padding: 1.5rem;
    margin: 2rem auto;
    max-width: 800px;
    box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
  }
  
  .changes-summary__title {
    font-size: 1.5rem;
    font-weight: 700;
    color: #e6f1ff;
    margin-bottom: 1.5rem;
    position: relative;
    padding-bottom: 0.75rem;
  }
  
  .changes-summary__title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 4rem;
    height: 3px;
    background-color: #64ffda;
  }
  
  .changes-group {
    margin-bottom: 1.5rem;
    padding: 1rem;
    background-color: #1e3a5f;
    border-radius: 8px;
    border: 1px solid rgba(100, 255, 218, 0.1);
  }
  
  .changes-group__title {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 1rem;
    display: flex;
    align-items: center;
  }
  
  .changes-group__title::before {
    content: '';
    display: inline-block;
    width: 0.75rem;
    height: 0.75rem;
    border-radius: 50%;
    margin-right: 0.5rem;
  }
  
  .changes-group__title.added {
    color: #64ffda;
  }
  
  .changes-group__title.added::before {
    background-color: #64ffda;
  }
  
  .changes-group__title.removed {
    color: #ff6b6b;
  }
  
  .changes-group__title.removed::before {
    background-color: #ff6b6b;
  }
  
  .changes-group__title.changed {
    color: #ffb86c;
  }
  
  .changes-group__title.changed::before {
    background-color: #ffb86c;
  }
  
  .changes-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  
  .changes-item {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem;
    border-radius: 6px;
    margin-bottom: 0.5rem;
    transition: all 0.2s ease;
  }
  
  .changes-item:last-child {
    margin-bottom: 0;
  }
  
  .changes-item.added {
    background-color: rgba(100, 255, 218, 0.1);
    border-left: 3px solid #64ffda;
  }
  
  .changes-item.removed {
    background-color: rgba(255, 107, 107, 0.1);
    border-left: 3px solid #ff6b6b;
  }
  
  .changes-item__name {
    font-weight: 600;
    color: #e6f1ff;
  }
  
  .changes-item__price {
    color: #a8b2d1;
  }
  
  .changes-jockey {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .changes-label {
    font-size: 0.85rem;
    color: #a8b2d1;
    margin-right: 0.5rem;
  }
  
  .changes-money {
    margin-top: 2rem;
    padding: 1rem;
    background-color: #1e3a5f;
    border-radius: 8px;
    border: 1px solid rgba(100, 255, 218, 0.1);
  }
  
  .changes-money__item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 0;
    border-bottom: 1px solid rgba(100, 255, 218, 0.1);
  }
  
  .changes-money__item:last-child {
    border-bottom: none;
    margin-top: 0.5rem;
    padding-top: 0.75rem;
    font-weight: 700;
  }
  
  .changes-money__label {
    color: #a8b2d1;
  }
  
  .changes-money__value {
    color: #e6f1ff;
  }
  
  .money-positive .changes-money__value {
    color: #64ffda;
  }
  
  .money-negative .changes-money__value {
    color: #ff6b6b;
  }
  
  /* Error message for overpriced team */
  .text-red-500 {
    color: #ff6b6b;
    font-weight: 600;
    margin-top: 0.75rem;
  }
  
  /* Confirmed Changes Section */
  .confirmed-changes {
    margin-top: 2rem;
    border: 1px solid rgba(100, 255, 218, 0.2);
    background-color: rgba(17, 34, 64, 0.8);
  }
  
  /* Clickable name styling */
  .clickable-name {
    display: flex;
    align-items: center;
    justify-content: space-between;
    cursor: pointer;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
    transition: all 0.2s ease;
  }
  
  .clickable-name:hover {
    background-color: rgba(100, 255, 218, 0.1);
    color: #64ffda;
  }
  
  .clickable-name:hover .info-icon {
    opacity: 1;
  }
  
  .clickable-name .info-icon {
    font-size: 0.85rem;
    opacity: 0.5;
    margin-left: 0.5rem;
    transition: opacity 0.2s ease;
  }
  
  .horse-name:hover, .jockey-name:hover {
    color: #64ffda;
    text-decoration: underline;
  }
  
  /* Racing badge styles */
  .racing-badge {
    display: inline-block;
    font-size: 0.7rem;
    font-weight: bold;
    padding: 2px 6px;
    border-radius: 4px;
    margin-left: 8px;
    vertical-align: middle;
  }
  
  .today-badge {
    background-color: #34d399;
    color: #0a192f;
  }
  
  .soon-badge {
    background-color: #60a5fa;
    color: #0a192f;
  }

  .horse-start-dates {
    color: #94a3b8;
    font-size: 0.78rem;
    margin: 0.1rem 0 0.25rem;
  }

  /* Disabled button styling */
  .disabled-button {
    opacity: 0.5;
    cursor: not-allowed !important;
    background-color: #8892b0 !important;
    color: #e6f1ff !important;
  }
  
  .disabled-button:hover {
    transform: none !important;
    box-shadow: none !important;
    background-color: #8892b0 !important;
  }
  
  /* Responsive adjustments */
  @media (max-width: 768px) {
    .changes-item {
      flex-direction: column;
      gap: 0.5rem;
    }
    
    .changes-item__price {
      align-self: flex-end;
    }
    
    .changes-jockey .changes-item {
      flex-direction: row;
      flex-wrap: wrap;
    }
  }

  /* Modal Base Styling */
  .modal {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(10, 25, 47, 0.85);
    display: flex;
    justify-content: center;
    align-items: center;
    backdrop-filter: blur(4px);
    z-index: 1000;
  }

  /* Modal Content Styling */
  .modal-content {
    background-color: #112240;
    padding: 2rem;
    border-radius: 12px;
    box-shadow: 0 10px 30px -10px rgba(2, 12, 27, 0.7);
    width: 90%;
    max-height: 90vh;
    display: flex;
    flex-direction: column;
    position: relative;
    border: 1px solid #1e3a5f;
    color: #ccd6f6;
  }
  /* Max width for selection modal */
  .modal[v-if="showSelectionModal"] .modal-content {
      max-width: 500px;
  }

  /* Close Button */
  .close-modal {
    background: none;
    border: none;
    font-size: 1.75rem;
    line-height: 1;
    position: absolute;
    top: 1rem;
    right: 1.5rem;
    cursor: pointer;
    color: #8892b0;
    transition: color 0.3s ease;
  }
  .close-modal:hover {
    color: #64ffda;
  }

  /* Modal List */
  .modal-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }
  .modal-list-item {
    padding: 0.75rem 0;
    border-bottom: 1px solid #1e3a5f;
  }
  .modal-list-item:last-child {
    border-bottom: none;
  }
  .modal-list-item button {
    padding: 0.5rem 1rem;
    cursor: pointer;
    background-color: #64ffda;
    color: #0a192f;
    border: none;
    border-radius: 4px;
    transition: all 0.3s ease;
  }
  .modal-list-item button:hover {
    background-color: #52d6c0;
  }

  /* Clickable names hover effect */
  .cursor-pointer {
    transition: all 0.2s ease-in-out;
  }
  .cursor-pointer:hover {
    color: #64ffda !important;
  }

  /* Loading spinner */
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .animate-spin {
    animation: spin 1s linear infinite;
  }

  /* ---------- Main Layout ---------- */
  /* Container for the entire team page */
  div > div[v-if="isAuthenticated"] {
    display: flex;
    flex-direction: column;
    max-width: 1000px;
    margin: 0 auto;
    padding: 0 1rem;
  }

  /* ---------- Player-info card ---------- */
  .player-info {
    margin: 0 auto 2.5rem;
    padding: 2rem;
    width: 100%;
    max-width: 800px;
    background-color: #112240 !important;
    border-radius: 12px;
    box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7);
    color: #ccd6f6;
    border: 1px solid #1e3a5f;
  }

  .player-info__title {
    font-size: 1.75rem;
    font-weight: 700;
    margin-bottom: 1.5rem;
    color: #e6f1ff;
    position: relative;
    padding-bottom: 0.75rem;
  }
  
  .player-info__title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 4rem;
    height: 3px;
    background-color: #64ffda;
  }

  /* Grid of 1→4 columns */
  .player-info__grid {
    display: grid;
    gap: 1.25rem;
    grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
    margin-bottom: 1.75rem;
  }

  .stat-item {
    background: #1e3a5f;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    display: flex;
    flex-direction: column;
    transition: transform 0.3s ease, box-shadow 0.3s ease;
    border: 1px solid rgba(100, 255, 218, 0.1);
  }
  
  .stat-item:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px -12px rgba(0, 0, 0, 0.7);
    border-color: rgba(100, 255, 218, 0.3);
  }

  .stat-label {
    font-size: 0.875rem;
    color: #a8b2d1;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }

  .stat-value {
    font-size: 1.5rem;
    font-weight: 700;
    margin-top: auto;
    color: #e6f1ff;
  }
  
  /* Styling for stat with image */
  .stat-with-image {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  
  .stat-icon {
    width: 30px;
    height: 30px;
    object-fit: contain;
  }
  
  @media (max-width: 768px) {
    .stat-icon {
      width: 25px;
      height: 25px;
    }
  }

  /* Toggle button */
  .player-info__toggle {
    display: inline-block;
    padding: 0.75rem 1.5rem;
    font-weight: 600;
    border-radius: 5px;
    background: #64ffda;
    color: #0a192f;
    border: none;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.2);
    margin-bottom: 0.75rem;
    cursor: pointer;
    letter-spacing: 0.5px;
  }
  
  .player-info__toggle:hover {
    background: #52d6c0;
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.25);
  }

  /* ---------- Points Breakdown ---------- */
  .points-breakdown {
    max-height: 24rem;
    overflow-y: auto;
    border-top: 1px solid #1e3a5f;
    padding-top: 1.25rem;
    margin-top: 0.5rem;
  }

  .points-breakdown__title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 1rem;
    color: #e6f1ff;
  }

  .points-breakdown__list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .points-breakdown__row {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.75rem 0.5rem;
    font-size: 0.9rem;
    border-bottom: 1px solid rgba(100, 255, 218, 0.1);
    transition: background-color 0.2s ease;
  }
  
  .points-breakdown__row:hover {
    background-color: rgba(100, 255, 218, 0.05);
  }
  
  .points-breakdown__row:last-child {
    border-bottom: none;
  }
  
  .points-breakdown__row.date-separator-top {
    border-top: 2px solid #1e3a5f;
    margin-top: 0.5rem;
    padding-top: 1rem;
  }

  .points-breakdown__empty {
    font-style: italic;
    color: #8892b0;
    padding: 1rem 0.5rem;
  }

  /* ---------- Smooth open/close animation ---------- */
  .fade-height-enter-active,
  .fade-height-leave-active {
    transition: max-height 0.4s ease, opacity 0.4s ease;
  }
  .fade-height-enter-from,
  .fade-height-leave-to {
    max-height: 0;
    opacity: 0;
  }

  .breakdown-link {
    color: #64ffda;
    cursor: pointer;
    font-weight: 500;
    text-decoration: none;
    transition: all 0.2s ease;
    margin: 0 2px;
    padding: 0.1rem 0.3rem;
    border-radius: 3px;
    position: relative;
    display: inline-flex;
    align-items: center;
  }
  
  .breakdown-link:hover {
    color: #52d6c0;
    background-color: rgba(100, 255, 218, 0.1);
  }
  
  .info-icon-small {
    font-size: 0.75rem;
    opacity: 0.7;
    margin-left: 0.25rem;
  }
  
  .breakdown-date-type {
    color: #a8b2d1;
    font-weight: 400;
    margin: 0 2px;
  }

  .pb-right {
    color: #64ffda;
    font-weight: 600;
  }
  
  /* Racing badge styles are kept below */
  
  /* Premium Upgrade Section Styles */
  .premium-upgrade-section {
    margin: 2rem auto;
    max-width: 600px;
  }

  .premium-upgrade-card {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    border-radius: 16px;
    padding: 2rem;
    color: white;
    box-shadow: 0 15px 35px rgba(102, 126, 234, 0.3);
    position: relative;
    overflow: hidden;
  }

  .premium-upgrade-card::before {
    content: '';
    position: absolute;
    top: -50%;
    right: -50%;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle, rgba(255,255,255,0.1) 0%, transparent 70%);
    pointer-events: none;
  }

  .premium-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1.5rem;
  }

  .premium-title {
    font-size: 1.5rem;
    font-weight: 700;
    margin: 0;
    text-shadow: 0 2px 4px rgba(0,0,0,0.3);
  }

  .premium-price {
    font-size: 2rem;
    font-weight: 800;
    background: rgba(255,255,255,0.2);
    padding: 0.5rem 1rem;
    border-radius: 12px;
    backdrop-filter: blur(10px);
  }

  .premium-benefits h4 {
    margin-bottom: 1rem;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .premium-benefits ul {
    list-style: none;
    padding: 0;
    margin: 0 0 1.5rem 0;
  }

  .premium-benefits li {
    padding: 0.5rem 0;
    font-size: 1rem;
    display: flex;
    align-items: center;
  }

  .premium-actions {
    text-align: center;
  }

  .premium-upgrade-btn {
    background: rgba(255,255,255,0.9);
    color: #667eea;
    border: none;
    padding: 1rem 2rem;
    border-radius: 12px;
    font-size: 1.1rem;
    font-weight: 700;
    cursor: pointer;
    transition: all 0.3s ease;
    box-shadow: 0 4px 15px rgba(0,0,0,0.2);
    backdrop-filter: blur(10px);
  }

  .premium-upgrade-btn:hover:not(:disabled) {
    background: white;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0,0,0,0.3);
  }

  .premium-upgrade-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  /* Premium Modal Styles */
  .premium-modal {
    max-width: 500px;
    width: 90%;
  }

  .premium-confirmation h3 {
    color: #667eea;
    margin-bottom: 1.5rem;
    text-align: center;
    font-size: 1.5rem;
  }

  .confirmation-details {
    background: #f8f9ff;
    padding: 1.5rem;
    border-radius: 12px;
    margin-bottom: 1.5rem;
    border-left: 4px solid #667eea;
  }

  .confirmation-details p {
    margin: 0.5rem 0;
    color: #4a5568;
  }

  .premium-benefits-modal {
    margin-bottom: 2rem;
  }

  .premium-benefits-modal h4 {
    color: #2d3748;
    margin-bottom: 1rem;
  }

  .premium-benefits-modal ul {
    list-style: none;
    padding: 0;
  }

  .premium-benefits-modal li {
    padding: 0.5rem 0;
    color: #4a5568;
    border-bottom: 1px solid #e2e8f0;
  }

  .premium-benefits-modal li:last-child {
    border-bottom: none;
  }

  .confirmation-buttons {
    display: flex;
    gap: 1rem;
    justify-content: center;
  }

  .confirm-premium-btn {
    background: #667eea;
    color: white;
    border: none;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    flex: 1;
    max-width: 200px;
  }

  .confirm-premium-btn:hover:not(:disabled) {
    background: #5a67d8;
    transform: translateY(-1px);
  }

  .confirm-premium-btn:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .cancel-premium-btn {
    background: transparent;
    color: #718096;
    border: 2px solid #e2e8f0;
    padding: 1rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    flex: 1;
    max-width: 200px;
  }

  .cancel-premium-btn:hover {
    background: #f7fafc;
    border-color: #cbd5e0;
  }

  /* Responsive adjustments for premium section */
  @media (max-width: 768px) {
    .premium-upgrade-card {
      padding: 1.5rem;
    }

    .premium-header {
      flex-direction: column;
      gap: 1rem;
      text-align: center;
    }

    .premium-title {
      font-size: 1.3rem;
    }

    .premium-price {
      font-size: 1.5rem;
    }

    .confirmation-buttons {
      flex-direction: column;
    }

    .confirm-premium-btn,
    .cancel-premium-btn {
      max-width: none;
    }
  }
  
  /* Race status alert */
  .race-status-alert {
    background-color: #ff8c00;
    color: #ffffff;
    padding: 12px 15px;
    border-radius: 8px;
    margin-bottom: 20px;
    font-weight: 600;
    display: flex;
    align-items: center;
    gap: 10px;
    animation: pulse 2s infinite;
  }
  
  /* Period ended or races completed alert */
  .race-status-alert.period-ended {
    background-color: #e53e3e;
    animation: none;
  }

  .race-status-icon {
    font-size: 1.5em;
  }

  @keyframes pulse {
    0% { opacity: 1; }
    50% { opacity: 0.8; }
    100% { opacity: 1; }
  }

  /* ---------- Selection Cards (Horses/Jockey) ---------- */
  .flex.flex-wrap.gap-4.mb-6 {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 2rem;
    margin-bottom: 2.5rem;
  }

  .selection {
    background-color: #112240 !important;
    border: 1px solid #1e3a5f !important;
    border-radius: 12px !important;
    padding: 1.5rem !important;
    box-shadow: 0 10px 30px -15px rgba(2, 12, 27, 0.7) !important;
    transition: transform 0.3s ease;
  }
  
  .selection:hover {
    transform: translateY(-5px);
  }

  .selection h2 {
    font-size: 1.5rem;
    font-weight: 700;
    margin-bottom: 1.25rem;
    color: #e6f1ff;
    position: relative;
    padding-bottom: 0.75rem;
  }
  
  .selection h2::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 3rem;
    height: 3px;
    background-color: #64ffda;
  }

  /* List items (horses/jockey) */
  .list {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-bottom: 1.25rem;
  }

  .list-item {
    background-color: #1e3a5f !important;
    border: 1px solid rgba(100, 255, 218, 0.1) !important;
    border-radius: 8px !important;
    padding: 1rem !important;
    transition: all 0.3s ease;
    position: relative;
  }
  

  
  .list-item:hover {
    border-color: rgba(100, 255, 218, 0.3) !important;
    box-shadow: 0 8px 20px -12px rgba(0, 0, 0, 0.7) !important;
  }

  .horse-item-content {
    display: flex;
    gap: 1rem;
    align-items: flex-start;
  }

  .horse-image {
    flex: 0 0 60px;
    width: 60px;
    height: 60px;
    overflow: hidden;
    border-radius: 6px;
    position: absolute;
    left: 1rem;
  }

  .horse-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  .horse-details {
    flex: 1;
    padding-left: 70px;
    width: 100%;
  }

  .list-item h3 {
    font-size: 1.1rem;
    font-weight: 600;
    margin-bottom: 0.5rem;
    color: #e6f1ff;
  }

  .list-item p {
    color: #a8b2d1 !important;
    margin-bottom: 0.75rem;
  }

  .list-item button {
    background-color: #64ffda;
    color: #0a192f;
    border: none;
    padding: 0.5rem 1rem;
    border-radius: 4px;
    font-weight: 600;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  }
  
  .list-item button:hover {
    background-color: #52d6c0;
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }

  /* Empty placeholder */
  .list-item.text-center.text-gray-400.italic {
    background-color: rgba(30, 58, 95, 0.5) !important;
    border: 1px dashed rgba(100, 255, 218, 0.2) !important;
    color: #8892b0 !important;
    display: flex;
    align-items: center;
    justify-content: center;
    min-height: 80px;
  }

  /* Add button */
  .add-button {
    background-color: #64ffda !important;
    color: #0a192f !important;
    border: none;
    padding: 0.75rem 1.25rem !important;
    border-radius: 6px;
    font-weight: 600;
    font-size: 1rem;
    transition: all 0.3s ease;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    text-transform: uppercase;
    letter-spacing: 0.5px;
  }
  
  .add-button:hover {
    background-color: #52d6c0 !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 10px rgba(0, 0, 0, 0.15);
  }

  /* Responsive adjustments */
  @media (max-width: 768px) {
    .player-info {
      padding: 1.5rem;
    }
    
    .player-info__grid {
      gap: 1rem;
      grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    }
    
    .stat-item {
      padding: 0.75rem 1rem;
    }
    
    .stat-value {
      font-size: 1.25rem;
    }
    
    .selection {
      padding: 1.25rem !important;
    }
  }

  @media (max-width: 480px) {
    .player-info__grid {
      grid-template-columns: 1fr 1fr;
    }
    
    .player-info__toggle {
      width: 100%;
      text-align: center;
    }
    
    .confirmation-buttons {
      flex-direction: column;
    }
    
    .confirmation-buttons button {
      width: 100%;
    }
  }

  /* EU Consumer Rights Compliance */
  .eu-compliance-section {
    margin: 1.5rem 0;
    padding: 1rem;
    background-color: rgba(100, 255, 218, 0.05);
    border: 1px solid rgba(100, 255, 218, 0.1);
    border-radius: 8px;
  }

  .checkbox-container {
    display: flex;
    align-items: flex-start;
    gap: 0.75rem;
    margin-bottom: 1rem;
  }

  .checkbox-container:last-child {
    margin-bottom: 0;
  }

  .checkbox-container input[type="checkbox"] {
    margin-top: 0.25rem;
    flex-shrink: 0;
  }

  .checkbox-label {
    color: #ccd6f6;
    font-size: 0.9rem;
    line-height: 1.4;
    cursor: pointer;
  }

  .terms-link-button {
    background: none;
    border: none;
    color: #64ffda;
    text-decoration: underline;
    cursor: pointer;
    font-size: inherit;
    padding: 0;
    margin: 0 2px;
    transition: color 0.3s ease;
  }

  .terms-link-button:hover {
    color: #52d6c0;
  }

  .confirm-premium-btn.eu-compliant:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    background-color: #8892b0 !important;
  }

  /* Premium Benefits Grid */
  .premium-benefits-modal {
    margin: 1.5rem 0;
    padding: 1rem;
    background-color: rgba(100, 255, 218, 0.08);
    border: 1px solid rgba(100, 255, 218, 0.2);
    border-radius: 8px;
  }

  .premium-benefits-modal h4 {
    color: #64ffda;
    font-size: 1.1rem;
    margin-bottom: 1rem;
    text-align: center;
  }

  .benefits-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 1rem;
  }

  .benefit-item {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 0.75rem;
    background-color: rgba(230, 241, 255, 0.05);
    border-radius: 6px;
    border: 1px solid rgba(100, 255, 218, 0.1);
    transition: all 0.3s ease;
  }

  .benefit-item:hover {
    background-color: rgba(100, 255, 218, 0.1);
    border-color: rgba(100, 255, 218, 0.3);
  }

  .benefit-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  .benefit-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .benefit-text strong {
    color: #e6f1ff;
    font-size: 0.95rem;
    font-weight: 600;
  }

  .benefit-text span {
    color: #a8b2d1;
    font-size: 0.85rem;
  }

  @media (max-width: 480px) {
    .benefits-grid {
      grid-template-columns: 1fr;
    }
  }

  /* Payment Notice Styling */
  .payment-notice {
    display: flex;
    gap: 1rem;
    background-color: rgba(100, 255, 218, 0.1);
    border-left: 4px solid #64ffda;
    padding: 1.25rem;
    border-radius: 8px;
    margin: 1rem 0;
  }

  .notice-icon {
    font-size: 1.5rem;
    flex-shrink: 0;
  }

  .notice-content p {
    margin-bottom: 0.75rem;
    color: #e6f1ff;
    font-weight: 600;
  }

  .notice-content ul {
    margin: 0;
    padding-left: 1.5rem;
    color: #a8b2d1;
  }

  .notice-content li {
    margin-bottom: 0.5rem;
    line-height: 1.4;
  }

  /* Unconfirmed Changes Banner */
  .unconfirmed-changes-banner {
    background: linear-gradient(135deg, #ff8c00, #ff6b00);
    border: 2px solid #ff6b00;
    border-radius: 8px;
    padding: 16px;
    margin-bottom: 24px;
    box-shadow: 0 4px 12px rgba(255, 140, 0, 0.3);
    animation: pulse-glow 2s infinite;
  }

  .banner-content {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .banner-icon {
    font-size: 2rem;
    flex-shrink: 0;
  }

  .banner-text h3 {
    color: white;
    margin: 0 0 4px 0;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .banner-text p {
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    font-size: 0.9rem;
  }

  .banner-confirm-btn {
    background: white;
    color: #ff6b00;
    border: none;
    padding: 12px 24px;
    border-radius: 6px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    white-space: nowrap;
  }

  .banner-confirm-btn:hover:not(:disabled) {
    background: #f0f0f0;
    transform: translateY(-1px);
  }

  .banner-confirm-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @keyframes pulse-glow {
    0%, 100% { box-shadow: 0 4px 12px rgba(255, 140, 0, 0.3); }
    50% { box-shadow: 0 4px 20px rgba(255, 140, 0, 0.5); }
  }

  /* Mobile Responsiveness for Banner */
  @media (max-width: 768px) {
    .banner-content {
      flex-direction: column;
      text-align: center;
      gap: 12px;
    }
    
    .banner-confirm-btn {
      width: 100%;
    }
  }
</style>
